class StoriesComponent extends HTMLElement {
  constructor() {
    super();

    this.modal = null;
    this.modalCloseButton = null;
    this.slider = null;
    this.sliderList = null;
    this.sliderObject = null;
    this.storyComponents = null;
  }

  connectedCallback() {
    this.modal = this.querySelector('[data-ref="stories-modal"]');
    this.modalCloseButton = this.querySelector('[data-ref="stories-modal-close"]');
    this.slider = this.querySelector('[data-ref="stories-slider"]');
    this.sliderList = this.querySelector('[data-ref="stories-slider-list"]');
    this.storyComponents = this.querySelectorAll('story-component');

    this.modal.addEventListener('click', this.#onModalBackdropClick);
    this.modalCloseButton.addEventListener('click', this.#closeModal);
  }

  disconnectedCallback() {
    this.modal.removeEventListener('click', this.#onModalBackdropClick);
    this.modalCloseButton.removeEventListener('click', this.#closeModal);
  }

  requestOpenStory(story_to_open) {
    const storyIndex = this.#getStoryIndexFromId(story_to_open);
    if (storyIndex === null) return;

    this.storyComponents.forEach((storyComponent) => {
      this.sliderList.appendChild(storyComponent.getStoryTemplateContent());
    });

    this.#openModal();

    requestAnimationFrame(() => {
      this.#mountSlider(storyIndex);
    });
  }

  requestNextStory() {
    const nextStoryIndex = this.sliderObject.Components.Controller.getNext();
    if (nextStoryIndex != -1) {
      this.sliderObject.go(nextStoryIndex);
    } else {
      this.#closeModal();
    }
  }

  #mountSlider(startIndex = 0) {
    this.sliderObject = new Splide(this.slider, {
      pagination: false,
      arrows: true,
      drag: false,
      snap: false,
      speed: 0,
      start: startIndex,
      breakpoints: {
        750: {
          arrows: true,
        },
      },
    });

    this.sliderObject.on('moved', (newIndex, prevIndex) => {
      this.storyComponents[prevIndex].destroyStory();
      this.storyComponents[newIndex].buildSlider();
    });

    this.sliderObject.mount();

    this.storyComponents[startIndex].buildSlider();

    requestAnimationFrame(() => {
      if (this.sliderObject) {
        this.sliderObject.refresh();
      }
    });
  }

  #getStoryIndexFromId(story_id) {
    let index = 0;
    for (const storyComponent of this.storyComponents) {
      if (storyComponent.storyId === story_id) {
        return index;
      }
      index++;
    }
    return null;
  }

  getStorySliderContent(story_id) {
    for (const storySlider of this.sliderList.querySelectorAll('[data-ref="story-slider"]')) {
      if (storySlider.dataset.storyId === story_id) {
        return storySlider;
      }
    }
    return null;
  }

  #onModalBackdropClick = (event) => {
    const rect = this.slider.getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog && !event.target.classList.contains('icon') && !event.target.classList.contains('splide__arrow')) {
      this.#closeModal();
    }
  };

  #openModal() {
    this.modal.showModal();
    document.body.classList.add('overflow-hidden');
  }

  #closeModal = () => {
    this.modal.close();
    document.body.classList.remove('overflow-hidden');

    this.#destroyModal();
  };

  #destroyModal() {
    this.storyComponents.forEach((storyComponent) => {
      storyComponent.destroyStory();
    });

    if (this.sliderObject) {
      this.sliderObject.destroy();
      this.sliderObject = null;
    }

    if (this.sliderList) {
      this.sliderList.innerHTML = '';
    }
  }
}

if (!customElements.get('stories-component')) {
  customElements.define('stories-component', StoriesComponent);
}

class StoryComponent extends HTMLElement {
  constructor() {
    super();

    this.storyId = null;
    this.story = null;
    this.storiesComponent = null;
    this.slider = null;
    this.sliderObject = null;
    this.progressInterval = null;
    this.nextStoryTimeout = null;
    this.#boundVideoEnded = null;
    this.#boundVideoTimeUpdate = null;
  }

  #boundVideoEnded;
  #boundVideoTimeUpdate;

  connectedCallback() {
    this.storyId = this.dataset.storyId;
    this.story = this.querySelector('[data-ref="story"]');
    this.storiesComponent = this.closest('stories-component');

    this.story.addEventListener('click', this.#handleStoryClick);
  }

  disconnectedCallback() {
    this.story.removeEventListener('click', this.#handleStoryClick);
  }

  getStoryTemplateContent() {
    return this.querySelector('[data-ref="story-content"]').content.cloneNode(true);
  }

  #handleStoryClick = () => {
    this.#openStory();
  };

  #openStory() {
    this.storiesComponent.requestOpenStory(this.storyId);
  }

  buildSlider() {
    if (this.sliderObject) return;

    this.slider = this.storiesComponent.getStorySliderContent(this.storyId);

    this.sliderObject = new Splide(this.slider, {
      pagination: false,
      arrows: true,
      drag: false,
      snap: false,
      pauseOnHover: false,
      pauseOnFocus: false,
      resetProgress: false,
      speed: 0,
    });

    this.sliderObject.on('moved', () => {
      this.#playStory();
    });

    this.sliderObject.mount();
    this.#playStory();
  }

  #playStory() {
    this.#clearTimers();
    this.#removeVideoListeners();

    const currentStoryMedia = this.#getCurrentStoryMedia();
    const currentStoryMediaProgressBar = this.slider.querySelector('[data-ref="story-progress-bar"][data-index="' + currentStoryMedia.dataset.index + '"]');
    const currentStoryMediaVideo = currentStoryMedia.querySelector('video');
    const isVideo = currentStoryMedia.dataset.type === 'video';

    this.#resetVideos(currentStoryMediaVideo);
    this.#resetProgressBars();
    this.#fillPreviousProgressBars(currentStoryMedia.dataset.index);

    if (isVideo && currentStoryMediaVideo) {
      this.#playVideoSlide(currentStoryMediaVideo, currentStoryMediaProgressBar, currentStoryMedia);
    } else {
      this.#playImageSlide(currentStoryMedia.dataset.interval, currentStoryMediaProgressBar);
    }
  }

  #playVideoSlide(video, progressBar, storyMedia) {
    const fallbackDuration = Number(storyMedia.dataset.interval) + 10000;

    this.#boundVideoTimeUpdate = () => {
      if (!video.duration) return;
      progressBar.value = (video.currentTime / video.duration) * 100;
    };

    this.#boundVideoEnded = () => {
      progressBar.value = 100;
      this.#advanceToNext();
    };

    video.addEventListener('timeupdate', this.#boundVideoTimeUpdate);
    video.addEventListener('ended', this.#boundVideoEnded);

    video.load();
    video.muted = false;
    video.addEventListener(
      'loadeddata',
      () => {
        video.play().catch(() => {});
      },
      { once: true },
    );

    this.nextStoryTimeout = setTimeout(() => {
      this.#advanceToNext();
    }, fallbackDuration);
  }

  #playImageSlide(duration, progressBar) {
    const interval = 20;

    this.progressInterval = setInterval(() => {
      progressBar.value += (interval / duration) * 100;
    }, interval);

    this.nextStoryTimeout = setTimeout(() => {
      this.#advanceToNext();
    }, duration);
  }

  #advanceToNext() {
    this.#clearTimers();
    this.#removeVideoListeners();

    const nextStoryIndex = this.sliderObject.Components.Controller.getNext();

    if (nextStoryIndex !== -1) {
      this.sliderObject.go(nextStoryIndex);
    } else {
      this.storiesComponent.requestNextStory();
    }
  }

  #fillPreviousProgressBars(currentIndex) {
    for (let i = 1; i < currentIndex; i++) {
      const progressBar = this.slider.querySelector('[data-ref="story-progress-bar"][data-index="' + i + '"]');
      if (progressBar) progressBar.value = 100;
    }
  }

  #clearTimers() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    if (this.nextStoryTimeout) {
      clearTimeout(this.nextStoryTimeout);
      this.nextStoryTimeout = null;
    }
  }

  #removeVideoListeners() {
    if (!this.slider) return;

    const currentMedia = this.slider.querySelectorAll('[data-ref="story-slide"]')[this.sliderObject?.index];
    const video = currentMedia?.querySelector('video');
    if (!video) return;

    if (this.#boundVideoTimeUpdate) {
      video.removeEventListener('timeupdate', this.#boundVideoTimeUpdate);
      this.#boundVideoTimeUpdate = null;
    }

    if (this.#boundVideoEnded) {
      video.removeEventListener('ended', this.#boundVideoEnded);
      this.#boundVideoEnded = null;
    }
  }

  destroyStory() {
    this.#clearTimers();
    this.#removeVideoListeners();
    this.#resetVideos();
    this.#resetProgressBars();

    if (this.sliderObject) {
      this.sliderObject.destroy();
      this.sliderObject = null;
    }
  }

  #resetProgressBars() {
    if (!this.slider) return;

    this.slider.querySelectorAll('[data-ref="story-progress-bar"]').forEach((progressBar) => {
      progressBar.value = 0;
    });
  }

  #resetVideos(excludeVideo = null) {
    if (!this.slider) return;

    this.slider.querySelectorAll('video').forEach((video) => {
      if (video === excludeVideo) return;
      video.pause();
      video.currentTime = 0;
      video.muted = true;
    });
  }

  #getCurrentStoryMedia() {
    return this.slider.querySelectorAll('[data-ref="story-slide"]')[this.sliderObject.index];
  }
}

if (!customElements.get('story-component')) {
  customElements.define('story-component', StoryComponent);
}
